// Sistem Yönetimi — ana kütüphaneler.
//
// Beş sekme: ünite sınıfları · stok kartı literatürü · eşik varsayılanları ·
// stok türleri · birimler.
//
// MODEL (kullanıcı kararı): kütüphane → kopya. Tenant'lar bu tabloları OKUMUYOR;
// yeni tesis açılırken içerik onların kendi tablolarına kopyalanıyor. Yani
// buradaki bir değişiklik MEVCUT tesisleri etkilemez — ancak süper admin
// Şirketler ekranından "Kütüphaneyi tamamla" derse eksikler eklenir, var olanın
// üzerine hiçbir zaman yazılmaz.
//
// Bu ekranın her yerinde o uyarı görünür durur: aksi hâlde kullanıcı bir eşiği
// düzeltip tüm tesislerde düzeldiğini sanardı.
//
// TEK İSTİSNA — BİRİMLER: birim bir şablon değil, ortak sözlük. Kopyalansaydı
// süper adminin eklediği birim var olan tesislerde hiç görünmezdi. Bu yüzden
// tesis tarafı /api/birimler ucundan CANLI okuyor (bkz. lib/birimler.ts) ve o
// sekme kendi uyarısını basıyor.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { tr } from '../../lib/trFormat'
import SistemKabuk from './SistemKabuk'
import UniteSinifiDrawer from './UniteSinifiDrawer'
import BirimDrawer from './BirimDrawer'
import {
  sistem,
  type SistemBirim,
  type SistemEsik,
  type SistemStokKarti,
  type SistemStokTuru,
  type SistemUniteSinifi,
} from './api'

type Sekme = 'sinif' | 'kart' | 'esik' | 'tur' | 'birim'

const GRUP_ADI: Record<string, string> = {
  on: 'Ön İşlem',
  cur: 'Çürütme',
  depo: 'Depolama',
  gaz: 'Gaz Hattı',
  enerji: 'Enerji',
}

const TUR_ADI: Record<string, string> = {
  hammadde: 'Hammadde',
  urun: 'Ürün',
  sarf: 'Sarf',
  yedek: 'Yedek',
  yakit: 'Yakıt',
}

const sayi = (v: number | null) => (v === null || v === undefined ? '—' : tr(v, 2))

/** Aktif/pasif anahtarı — kütüphanede silmek yerine kapatmak tercih ediliyor. */
function AktifHucre({
  aktif,
  degistir,
  bekliyor,
}: {
  aktif: boolean
  degistir: () => void
  bekliyor: boolean
}) {
  return (
    <button
      className={aktif ? 'badge b-navy' : 'badge b-gray'}
      type="button"
      disabled={bekliyor}
      title={
        aktif
          ? 'Açık — yeni tesislere kopyalanır. Kapatmak için tıklayın.'
          : 'Kapalı — yeni tesislere kopyalanmaz. Açmak için tıklayın.'
      }
      onClick={degistir}
      style={{ cursor: 'pointer' }}
    >
      {aktif ? 'Açık' : 'Kapalı'}
    </button>
  )
}

/**
 * Kopya modelinin uyarısı. `birim` sekmesi TEK İSTİSNA: birimler kopyalanmıyor,
 * ortak sözlük olarak okunuyor. Aynı metni orada da basmak yanlış olurdu —
 * kullanıcı eklediği birimin mevcut tesislerde görünmediğini sanırdı.
 */
function Uyari({ birim = false }: { birim?: boolean }) {
  if (birim) {
    return (
      <div className="info-box" style={{ margin: 12 }}>
        <b>Birimler tüm sistemde ortaktır.</b>
        <span>
          Diğer kütüphanelerin aksine birimler tesise <b>kopyalanmaz</b>: buradaki
          değişiklik <b>mevcut tesisler dâhil</b> her birim listesine anında
          yansır. Kullanımdaki bir birim silinemez — silmek yerine kapatın.
        </span>
      </div>
    )
  }

  return (
    <div className="info-box" style={{ margin: 12 }}>
      <b>Kütüphane bir şablondur.</b>
      <span>
        Buradaki değişiklikler <b>mevcut tesisleri etkilemez</b> — yalnız bundan
        sonra açılacak tesislere kopyalanır. Var olan bir şirketi tamamlamak için
        Şirketler ekranındaki "Kütüphaneyi tamamla" düğmesini kullanın; o da eksik
        olanı ekler, tesisin kendi değerinin üzerine yazmaz.
      </span>
    </div>
  )
}

// ------------------------------------------------------------- ünite sınıfları

function SiniflarSekmesi() {
  const kuyruk = useQueryClient()
  const { ac } = useDrawer()
  const { data = [], isLoading } = useQuery({
    queryKey: ['sistem-kut-sinif'],
    queryFn: sistem.uniteSiniflari,
  })

  /**
   * Şablondaki satır sayısı — tabloda "kaç parametre" diye görünüyor.
   * Adı `sayi` DEĞİL: modül düzeyinde sayı biçimleyen bir `sayi` var, aynı adı
   * kullanmak onu gölgeleyip okuyanı yanıltırdı.
   */
  const satirSayisi = (ham: string | null | undefined) => {
    if (!ham?.trim()) return 0
    try {
      const d = JSON.parse(ham)
      return Array.isArray(d) ? d.length : 0
    } catch {
      return 0
    }
  }

  const duzenle = (x?: SistemUniteSinifi) =>
    ac({
      baslik: x ? `${x.kodPrefix} — ${x.ad}` : 'Yeni ünite sınıfı',
      altBaslik: 'Teknik alanlar ve beyan parametreleri',
      boyut: 'lg',
      icerik: <UniteSinifiDrawer mevcut={x} />,
    })

  const degistir = useMutation({
    mutationFn: (x: SistemUniteSinifi) =>
      sistem.uniteSinifiYaz(x.id, { ...x, aktif: !x.aktif }),
    onSuccess: (x) => {
      kuyruk.invalidateQueries({ queryKey: ['sistem-kut-sinif'] })
      toast(`${x.kodPrefix} · ${x.aktif ? 'açıldı' : 'kapatıldı'}`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Değiştirilemedi'),
  })

  return (
    <>
      <Uyari />
      <div className="toolbar">
        <div className="toolbar-right" style={{ marginRight: 'auto' }}>
          <span>{tr(data.length, 0)} sınıf</span>
        </div>
        <button className="btn btn-pri btn-sm" type="button" onClick={() => duzenle()}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Sınıf ekle
        </button>
      </div>
      <table className="grid">
        <thead>
          <tr>
            <th style={{ width: 84 }}>Önek</th>
            <th style={{ width: '22%' }}>Ad</th>
            <th style={{ width: 110 }}>Grup</th>
            <th>Açıklama</th>
            <th className="num" style={{ width: 96 }}>Beyan par.</th>
            <th className="num" style={{ width: 88 }}>Teknik</th>
            <th className="num" style={{ width: 62 }}>Sıra</th>
            <th style={{ width: 80 }}>Durum</th>
            <th style={{ width: 86 }} />
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={9} className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</td>
            </tr>
          )}
          {data.map((x) => (
            <tr key={x.id} style={{ cursor: 'pointer' }} onClick={() => duzenle(x)}>
              <td><span className="kod">{x.kodPrefix}</span></td>
              <td><div className="cell-name">{x.ad}</div></td>
              <td className="cell-sub">{GRUP_ADI[x.grup] ?? x.grup}</td>
              <td className="cell-sub" style={{ fontSize: 10.5 }}>{x.aciklama ?? '—'}</td>
              <td className="num" style={{ fontWeight: 600 }}>{tr(satirSayisi(x.bSablon), 0)}</td>
              <td className="num">{tr(satirSayisi(x.tSablon), 0)}</td>
              <td className="num">{tr(x.sira, 0)}</td>
              <td onClick={(e) => e.stopPropagation()}>
                <AktifHucre
                  aktif={x.aktif}
                  bekliyor={degistir.isPending}
                  degistir={() => degistir.mutate(x)}
                />
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost btn-sm" type="button" onClick={() => duzenle(x)}>
                    Düzenle
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid-foot">
        <span>
          {tr(data.length, 0)} sınıf · K25 · satıra tıklayın: beyan parametrelerini
          düzenleyin ya da yenisini ekleyin
        </span>
      </div>
    </>
  )
}

// ------------------------------------------------------------ stok kartları

function KartlarSekmesi() {
  const kuyruk = useQueryClient()
  const [tur, setTur] = useState<'tumu' | string>('tumu')

  const { data = [], isLoading } = useQuery({
    queryKey: ['sistem-kut-kart'],
    queryFn: sistem.stokKartlari,
  })

  const degistir = useMutation({
    mutationFn: (x: SistemStokKarti) => sistem.stokKartiYaz(x.id, { ...x, aktif: !x.aktif }),
    onSuccess: (x) => {
      kuyruk.invalidateQueries({ queryKey: ['sistem-kut-kart'] })
      toast(`${x.kod} · ${x.aktif ? 'açıldı' : 'kapatıldı'}`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Değiştirilemedi'),
  })

  const liste = tur === 'tumu' ? data : data.filter((x) => x.tur === tur)

  return (
    <>
      <Uyari />
      <div className="toolbar">
        <div className="seg">
          <button
            type="button"
            className={tur === 'tumu' ? 'seg-i on' : 'seg-i'}
            onClick={() => setTur('tumu')}
          >
            Tümü<small>{data.length}</small>
          </button>
          {Object.entries(TUR_ADI).map(([k, ad]) => (
            <button
              key={k}
              type="button"
              className={tur === k ? 'seg-i on' : 'seg-i'}
              onClick={() => setTur(k)}
            >
              {ad}
              <small>{data.filter((x) => x.tur === k).length}</small>
            </button>
          ))}
        </div>
      </div>

      <table className="grid">
        <thead>
          <tr>
            <th style={{ width: 92 }}>Kod</th>
            <th>Ad</th>
            <th style={{ width: 96 }}>Tür</th>
            <th style={{ width: 58 }}>Birim</th>
            <th className="num" style={{ width: 68 }}>KM %</th>
            <th className="num" style={{ width: 72 }}>oKM %</th>
            <th className="num" style={{ width: 84 }}>Verim</th>
            <th className="num" style={{ width: 74 }}>BMP</th>
            <th style={{ width: 84 }}>Durum</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={9} className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</td>
            </tr>
          )}
          {liste.map((x) => (
            <tr key={x.id}>
              <td><span className="kod">{x.kod}</span></td>
              <td>
                <div className="cell-name">
                  {x.ad}
                  {x.hij && <span className="badge b-amber" title="K19 hijyen bayrağı">Hij</span>}
                </div>
                {x.grup && <div className="cell-sub">{x.grup}</div>}
              </td>
              <td className="cell-sub">{TUR_ADI[x.tur] ?? x.tur}</td>
              <td className="cell-sub">{x.birim}</td>
              <td className="num">{sayi(x.km)}</td>
              <td className="num">{sayi(x.okmKm)}</td>
              <td className="num">{sayi(x.verimNlKgOkm)}</td>
              <td className="num">{sayi(x.metanVerimiBmp)}</td>
              <td>
                <AktifHucre
                  aktif={x.aktif}
                  bekliyor={degistir.isPending}
                  degistir={() => degistir.mutate(x)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid-foot">
        <span>
          {tr(liste.length, 0)} kart · K5 literatür kütüphanesi · kopya tesiste
          "literatür" kaynağıyla açılır (K13)
        </span>
      </div>
    </>
  )
}

// -------------------------------------------------------------------- eşikler

function EsiklerSekmesi() {
  const kuyruk = useQueryClient()
  const { data = [], isLoading } = useQuery({
    queryKey: ['sistem-kut-esik'],
    queryFn: sistem.esikler,
  })

  const degistir = useMutation({
    mutationFn: (x: SistemEsik) => sistem.esikYaz(x.id, { ...x, aktif: !x.aktif }),
    onSuccess: (x) => {
      kuyruk.invalidateQueries({ queryKey: ['sistem-kut-esik'] })
      toast(`${x.gosterge} · ${x.aktif ? 'açıldı' : 'kapatıldı'}`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Değiştirilemedi'),
  })

  return (
    <>
      <Uyari />
      <table className="grid">
        <thead>
          <tr>
            <th>Gösterge</th>
            <th className="num" style={{ width: 110 }}>Normal min</th>
            <th className="num" style={{ width: 110 }}>Normal maks</th>
            <th className="num" style={{ width: 96 }}>Amber</th>
            <th className="num" style={{ width: 96 }}>Kırmızı</th>
            <th style={{ width: 84 }}>Durum</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={6} className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</td>
            </tr>
          )}
          {data.map((x) => (
            <tr key={x.id}>
              <td><span className="kod">{x.gosterge}</span></td>
              <td className="num">{sayi(x.normalMin)}</td>
              <td className="num">{sayi(x.normalMax)}</td>
              <td className="num">{sayi(x.amber)}</td>
              <td className="num">{sayi(x.kirmizi)}</td>
              <td>
                <AktifHucre
                  aktif={x.aktif}
                  bekliyor={degistir.isPending}
                  degistir={() => degistir.mutate(x)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid-foot">
        <span>
          {tr(data.length, 0)} gösterge · §6.3 fabrika değerleri · tesis kendi
          değerini Ayarlar'dan yazar
        </span>
      </div>
    </>
  )
}

// ---------------------------------------------------------------- stok türleri

function TurlerSekmesi() {
  const kuyruk = useQueryClient()
  const { data = [], isLoading } = useQuery({
    queryKey: ['sistem-kut-tur'],
    queryFn: sistem.stokTurleri,
  })

  const degistir = useMutation({
    mutationFn: (x: SistemStokTuru) =>
      sistem.stokTuruYaz(x.id, {
        etiket: x.etiket,
        aciklama: x.aciklama,
        kodOneki: x.kodOneki,
        sira: x.sira,
        aktif: !x.aktif,
      }),
    onSuccess: (x) => {
      kuyruk.invalidateQueries({ queryKey: ['sistem-kut-tur'] })
      toast(`${x.etiket} · ${x.aktif ? 'açıldı' : 'kapatıldı'}`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Değiştirilemedi'),
  })

  return (
    <>
      <div className="info-box" style={{ margin: 12 }}>
        <b>Tür kodları değiştirilemez.</b>
        <span>
          Beş kod (<span className="mono">hammadde · urun · sarf · yedek · yakit</span>)
          hesap motorunda dallanma noktası: besleme yalnız hammadde tüketir, yakıt
          filo dolumuna, ürün SVK sevkine bağlıdır. Kod veriden yönetilseydi bir
          türü silmek besleme raporunu sessizce yanlış hesaplatırdı. Burada
          yönetilen: etiket, kod öneki, sıra ve görünürlük.
        </span>
      </div>
      <table className="grid">
        <thead>
          <tr>
            <th style={{ width: 120 }}>Kod</th>
            <th style={{ width: 160 }}>Etiket</th>
            <th style={{ width: 100 }}>Kart öneki</th>
            <th>Açıklama</th>
            <th className="num" style={{ width: 70 }}>Sıra</th>
            <th style={{ width: 84 }}>Durum</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={6} className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</td>
            </tr>
          )}
          {data.map((x) => (
            <tr key={x.id}>
              <td><span className="mono">{x.tur}</span></td>
              <td><div className="cell-name">{x.etiket}</div></td>
              <td><span className="kod">{x.kodOneki}</span></td>
              <td className="cell-sub" style={{ fontSize: 10.5 }}>{x.aciklama ?? '—'}</td>
              <td className="num">{tr(x.sira, 0)}</td>
              <td>
                <AktifHucre
                  aktif={x.aktif}
                  bekliyor={degistir.isPending}
                  degistir={() => degistir.mutate(x)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid-foot">
        <span>Kapalı tür yeni kart formunda seçenek olarak çıkmaz; var olan kartlar etkilenmez</span>
      </div>
    </>
  )
}

// -------------------------------------------------------------------- birimler

function BirimlerSekmesi() {
  const kuyruk = useQueryClient()
  const { ac } = useDrawer()
  const { data = [], isLoading } = useQuery({
    queryKey: ['sistem-kut-birim'],
    queryFn: sistem.birimler,
  })

  const duzenle = (x?: SistemBirim) =>
    ac({
      baslik: x ? `${x.kod} — ${x.ad}` : 'Yeni birim',
      altBaslik: 'Tüm sistemde ortak birim sözlüğü',
      icerik: (
        <BirimDrawer
          mevcut={x}
          sonrakiSira={data.reduce((e, b) => Math.max(e, b.sira), 0) + 10}
        />
      ),
    })

  const degistir = useMutation({
    mutationFn: (x: SistemBirim) => sistem.birimYaz(x.id, { ...x, aktif: !x.aktif }),
    onSuccess: (x) => {
      kuyruk.invalidateQueries({ queryKey: ['sistem-kut-birim'] })
      toast(`${x.kod} · ${x.aktif ? 'açıldı' : 'kapatıldı'}`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Değiştirilemedi'),
  })

  return (
    <>
      <Uyari birim />
      <div className="toolbar">
        <div className="toolbar-right" style={{ marginRight: 'auto' }}>
          <span>{tr(data.length, 0)} birim</span>
        </div>
        <button className="btn btn-pri btn-sm" type="button" onClick={() => duzenle()}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Birim ekle
        </button>
      </div>
      <table className="grid">
        <thead>
          <tr>
            <th style={{ width: 110 }}>Kod</th>
            <th>Ad</th>
            <th style={{ width: 140 }}>Tür kısıtı</th>
            <th className="num" style={{ width: 70 }}>Sıra</th>
            <th style={{ width: 84 }}>Durum</th>
            <th style={{ width: 86 }} />
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={6} className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</td>
            </tr>
          )}
          {data.map((x) => (
            <tr key={x.id}>
              <td><span className="kod">{x.kod}</span></td>
              <td><div className="cell-name">{x.ad}</div></td>
              <td className="cell-sub">{x.tur ? (TUR_ADI[x.tur] ?? x.tur) : 'Tüm türler'}</td>
              <td className="num">{tr(x.sira, 0)}</td>
              <td>
                <AktifHucre
                  aktif={x.aktif}
                  bekliyor={degistir.isPending}
                  degistir={() => degistir.mutate(x)}
                />
              </td>
              <td>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => duzenle(x)}>
                  Düzenle
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid-foot">
        <span>
          {tr(data.length, 0)} birim · stok kartı formundan manuel harekete kadar
          tüm birim listelerini besler
        </span>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------- ekran

function Icerik() {
  const [sekme, setSekme] = useState<Sekme>('sinif')

  const sekmeler: { anahtar: Sekme; etiket: string; icerik: React.ReactNode }[] = [
    { anahtar: 'sinif', etiket: 'Ünite Sınıfları', icerik: <SiniflarSekmesi /> },
    { anahtar: 'kart', etiket: 'Stok Kartı Literatürü', icerik: <KartlarSekmesi /> },
    { anahtar: 'esik', etiket: 'Eşik Varsayılanları', icerik: <EsiklerSekmesi /> },
    { anahtar: 'tur', etiket: 'Stok Türleri', icerik: <TurlerSekmesi /> },
    { anahtar: 'birim', etiket: 'Birimler', icerik: <BirimlerSekmesi /> },
  ]

  const etkin = sekmeler.find((s) => s.anahtar === sekme) ?? sekmeler[0]

  return (
    <>
      <div className="crumb">
        Sistem Yönetimi <b>/ Kütüphane</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">Ana Kütüphaneler</h1>
      </div>

      <div className="card">
        <div className="tabbar">
          {sekmeler.map((s) => (
            <button
              key={s.anahtar}
              type="button"
              className={etkin.anahtar === s.anahtar ? 'tab on' : 'tab'}
              onClick={() => setSekme(s.anahtar)}
            >
              {s.etiket}
            </button>
          ))}
        </div>
        {etkin.icerik}
      </div>
    </>
  )
}

export default function KutuphaneSayfasi() {
  return (
    <SistemKabuk>
      <Icerik />
    </SistemKabuk>
  )
}
